import { useCallback, useEffect, useMemo, useState } from "react";

import clsx from "clsx";

import { gql } from "@apollo/client";
import { GenericExternalLink } from "@components/generic-external-link";
import { useFeatureFlags, useNorthbeamMutation } from "@hooks/http";
import { type RefreshPixelAMIs } from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { PixelDiagnosticsChart } from "@pages/settings/pixel-diagnostics";
import { Collapsible } from "@pages/subscriptions-order-tags/subscription-order-tags";
import { PrimaryButton } from "@shared/buttons";
import Spinner from "@shared/spinner";

import { InfoPanel } from "../components/info-panel";

export const VERIFY_SETUP_TITLE = "Test your pixel setup";

export type PixelVerificationStatus = {
  isPixelInstalled?: boolean;
  isConversionTracking?: boolean;
};

export const VerifySetup = ({
  subStep,
  subStepRef,
  isSetupConfirmed,
  setSetupConfirmed,
  pixelVerification,
  setPixelVerification,
  domainName,
  disabled,
}: {
  subStep: "c" | "d";
  subStepRef: (node: any) => void;
  isSetupConfirmed: boolean;
  setSetupConfirmed: (isConfirmed: boolean) => void;
  pixelVerification?: PixelVerificationStatus;
  setPixelVerification: (status?: PixelVerificationStatus) => void;
  domainName?: string;
  disabled?: boolean;
}) => {
  const [isConfirmed, _setIsConfirmed] = useState(isSetupConfirmed);
  const { hasFeatureFlag } = useFeatureFlags();
  const setIsConfirmed = useCallback(
    (isConfirmed: boolean) => {
      _setIsConfirmed(isConfirmed);
      setSetupConfirmed(isConfirmed);
    },
    [setSetupConfirmed],
  );

  const [refreshAMIs, { loading, data }] =
    useNorthbeamMutation<RefreshPixelAMIs>(REFRESH_AMIS, { useHeavy: true });

  useEffect(() => {
    // if setup confirmed but no verification status persisted, then assume user confirmed but closed page before verification completed, so retry
    if (isSetupConfirmed && pixelVerification === undefined) {
      refreshAMIs();
    }
  }, [refreshAMIs]);

  useEffect(() => {
    if (loading || !data) {
      return;
    }
    setPixelVerification({
      isPixelInstalled: data.refreshTestUrlsForNBPixelSetupAMI,
      isConversionTracking: data.refreshConversionPixelAMI,
    });
  }, [loading, data]);
  const isVerified = useMemo(
    () =>
      !loading &&
      ((data?.refreshConversionPixelAMI &&
        data?.refreshTestUrlsForNBPixelSetupAMI) ||
        (pixelVerification &&
          pixelVerification.isConversionTracking &&
          pixelVerification.isPixelInstalled)),
    [data, pixelVerification, loading],
  );

  const confirm = useCallback(() => {
    setIsConfirmed(true);

    refreshAMIs();
  }, [refreshAMIs, setIsConfirmed]);
  const [expandedHelp, setExpandedHelp] = useState(false);

  return (
    <div
      ref={subStepRef}
      className="pb-20 border-b border-gray-0 px-10 pt-10 scroll-m-10"
    >
      <div className="text-2xl font-semibold leading-loose">
        7{subStep}. {VERIFY_SETUP_TITLE}
      </div>
      <PrimaryButton
        onClick={confirm}
        loading={loading}
        className="w-64 h-12 text-lg mt-5 font-semibold"
      >
        TEST PIXEL
      </PrimaryButton>
      {isConfirmed && (
        <>
          <div className="mt-6">
            <div className="mb-2 text-lg font-semibold leading-loose inline-flex gap-2">
              <span>Pixel setup verification</span>
              <div
                className={clsx(
                  "px-2 rounded-3xl justify-start items-center gap-1 inline-flex uppercase font-semibold text-sm",
                  {
                    "bg-interstellar-3": !isSetupConfirmed || loading,
                    "bg-alien-2": isVerified && !loading && isSetupConfirmed,
                    "bg-mars-2": !isVerified && !loading && isSetupConfirmed,
                  },
                )}
              >
                <StatusIcon
                  isVerified={isVerified}
                  isSetupConfirmed={isSetupConfirmed}
                  loading={loading}
                />
                <span>
                  {(!isSetupConfirmed || loading) && "pending"}
                  {isSetupConfirmed && isVerified && "success"}
                  {isSetupConfirmed && !loading && !isVerified && "failed"}
                </span>
              </div>
            </div>
            {!isConfirmed && (
              <div>
                After your pixel setup is confirmed, we will verify the pixel is
                installed correctly on your store.
              </div>
            )}
            {isConfirmed && (
              <div>
                {loading && (
                  <div className="flex gap-2 items-center">
                    <Spinner colorClassname="font-black" />
                    <span>
                      Verifying pixel setup. This may take a few minutes, please
                      stay on the page until verification is complete.
                    </span>
                  </div>
                )}
                {!loading && (
                  <>
                    <div className="flex gap-2 items-center justify-start font-semibold">
                      <StatusIcon
                        isVerified={pixelVerification?.isPixelInstalled}
                        isSetupConfirmed={isSetupConfirmed}
                        loading={loading}
                      />
                      {pixelVerification?.isPixelInstalled ? (
                        <span>Pixel is installed correctly</span>
                      ) : (
                        <span>Pixel is not installed correctly</span>
                      )}
                    </div>
                    {!pixelVerification?.isPixelInstalled && (
                      <div className="w-full max-w-[800px] my-2 py-4 px-4 border rounded bg-sun-2 leading-normal font-semibold">
                        <i className="mr-2 fa-regular fa-warning text-sun+2 font-black" />
                        Pixel not detected on landing page for your shop at{" "}
                        <GenericExternalLink href={domainName || ""}>
                          {domainName}
                        </GenericExternalLink>
                      </div>
                    )}
                    <div className="flex gap-2 items-center justify-start font-semibold">
                      <StatusIcon
                        isVerified={pixelVerification?.isConversionTracking}
                        isSetupConfirmed={isSetupConfirmed}
                        loading={loading}
                      />
                      {pixelVerification?.isConversionTracking ? (
                        <span>Conversion event successfully tracked</span>
                      ) : (
                        <span>
                          No conversion event found{" "}
                          <span
                            className="hover:underline text-primary cursor-pointer"
                            onClick={() => setExpandedHelp(true)}
                          >
                            Click here to open troubleshooting
                          </span>
                        </span>
                      )}
                    </div>
                    {!pixelVerification?.isConversionTracking && (
                      <div className="w-full max-w-[800px] my-2 py-4 px-4 border rounded bg-sun-2 leading-normal font-semibold">
                        <i className="mr-2 fa-regular fa-warning text-sun+2 font-black" />
                        A purchase must be made after the pixel is installed and
                        conversion tracking is configured
                      </div>
                    )}
                  </>
                )}
                {!loading && isVerified !== undefined && !isVerified && (
                  <div className="mt-4">
                    <div className="text-lg">
                      We were not able to determine that the pixel is correctly
                      setup on your store. Please resolve the above errors and
                      try again.
                    </div>
                    <PrimaryButton
                      className="my-1 w-fit font-semibold uppercase"
                      onClick={() => {
                        refreshAMIs();
                      }}
                      disabled={loading}
                    >
                      <i className="mr-2 fa-sharp fa-regular fa-rotate font-black" />
                      Retry
                    </PrimaryButton>
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <div
              className={clsx(
                "flex items-center h-10  bg-white  cursor-pointer border-b rounded-none pb-3 mt-5",
              )}
              onClick={() => setExpandedHelp(!expandedHelp)}
            >
              <div className="text-lg font-semibold ">Pixel Diagnostics</div>
              <i
                className={clsx("fa-regular fa-sharp mr-2 text-primary ml-5", {
                  "fa-chevron-down": expandedHelp,
                  "fa-chevron-up": !expandedHelp,
                })}
              />
            </div>
            <Collapsible expanded={expandedHelp}>
              <div className="mt-5" />
              <PixelDiagnosticsChart />
              <InfoPanel
                label="Need help with verifying your pixel setup?"
                className="mt-5 py-4 px-6"
              >
                <GenericExternalLink
                  href="https://www.northbeam.io/submit-a-support-ticket"
                  className="border border-gray-0 bg-white px-4 py-[6px] inline-flex items-center gap-2 rounded-2xl no-underline
                  hover:bg-cosmic-3 hover:border-cosmic+1"
                >
                  <i
                    className="fa-envelope fa-regular"
                    style={{ color: "#7081FF" }}
                  ></i>
                  <div className="text-black uppercase font-semibold text-xs">
                    Contact us
                  </div>
                </GenericExternalLink>
              </InfoPanel>
            </Collapsible>
          </div>
        </>
      )}
    </div>
  );
};

const StatusIcon = ({
  isVerified,
  isSetupConfirmed,
  loading,
}: {
  isVerified?: boolean;
  isSetupConfirmed: boolean;
  loading: boolean;
}) => (
  <i
    className={clsx("text-base", {
      "fa-regular fa-x text-mars+2 font-black":
        !isVerified && isSetupConfirmed && !loading,
      "fa-regular fa-check text-alien+0 font-black":
        isVerified && isSetupConfirmed && !loading,
      "fa-solid fa-circle-notch ": !isSetupConfirmed || loading,
    })}
  />
);

export const REFRESH_AMIS = gql`
  mutation RefreshPixelAMIs {
    refreshConversionPixelAMI
    refreshTestUrlsForNBPixelSetupAMI
  }
`;
